import { useState } from 'react'
import type { SearchHit } from '../api/types'
import { api } from '../api/client'
import { radius } from '../components/primitives/shape'
import { AgentName } from './Stream'

/**
 * What a reader sees next to a hit: whether it matched on the words
 * themselves or on what they meant. Lexical is the senior half, so this is
 * real information, not decoration. An exact token match and a semantic
 * neighbour deserve different trust.
 */
const FOUND_BY_LABEL: Record<SearchHit['found_by'], string> = {
  lexical: 'exact match',
  semantic: 'similar meaning',
  both: 'exact match + similar meaning',
}

/**
 * A search field over the stream.
 *
 * Submits on enter rather than as you type: a search hits the server, and a
 * field that fires on every keystroke either floods it or needs a debounce to
 * pretend it does not.
 *
 * Results are headlines, never bodies, the same rule the endpoint enforces.
 * A hit exists to say which message to open, not to stand in for it, so
 * `onOpenMessage` is the only thing a result can do.
 */
export function SearchBar({
  channelId,
  onOpenMessage,
  onClose,
}: {
  channelId: string
  onOpenMessage: (messageId: string) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<SearchHit[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [failed, setFailed] = useState(false)

  async function runSearch(event: React.FormEvent) {
    event.preventDefault()
    const trimmed = query.trim()
    if (!trimmed) {
      setHits(null)
      return
    }
    setSearching(true)
    setFailed(false)
    try {
      setHits(await api.search(channelId, trimmed))
    } catch {
      setHits(null)
      setFailed(true)
    } finally {
      setSearching(false)
    }
  }

  return (
    <div
      style={{
        flex: 'none', display: 'flex', flexDirection: 'column', gap: 8,
        padding: '10px 22px', borderBottom: '1px solid var(--border)',
        background: 'var(--panel)',
      }}
    >
      <form
        role="search"
        onSubmit={(event) => void runSearch(event)}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <input
          type="search"
          aria-label="Search this room"
          placeholder="Search this room"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoFocus
          style={{
            flex: 1, minWidth: 0, background: 'var(--panel-strong)',
            border: '1px solid var(--border-strong)', borderRadius: radius.sm,
            padding: '7px 12px', color: 'var(--text)',
            font: '400 13px var(--font-interface)',
          }}
        />
        <button
          type="submit"
          disabled={searching}
          style={{
            flex: 'none', cursor: searching ? 'default' : 'pointer',
            border: '1px solid var(--border-strong)', background: 'var(--panel)',
            borderRadius: radius.action, padding: '6px 12px',
            color: 'var(--text-soft)', font: '600 12px var(--font-interface)',
          }}
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
        <button
          type="button"
          aria-label="Close search"
          onClick={onClose}
          style={{
            flex: 'none', cursor: 'pointer', color: 'var(--text-faint)',
            font: '400 18px var(--font-interface)', lineHeight: 1, padding: '4px 6px',
          }}
        >
          ×
        </button>
      </form>

      {failed && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-dim)' }}>
          Could not search this room right now.
        </p>
      )}

      {hits !== null && !failed && (
        hits.length === 0 ? (
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-dim)' }}>
            No messages match &ldquo;{query.trim()}&rdquo;.
          </p>
        ) : (
          <div
            role="listbox"
            aria-label="Search results"
            style={{
              display: 'flex', flexDirection: 'column', gap: 2,
              maxHeight: 220, overflowY: 'auto',
            }}
          >
            {hits.map((hit) => (
              <button
                key={hit.id}
                role="option"
                aria-selected={false}
                onClick={() => onOpenMessage(hit.id)}
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 10,
                  textAlign: 'left', cursor: 'pointer', padding: '7px 8px',
                  borderRadius: radius.sm, background: 'transparent',
                }}
                className="sp-tap"
              >
                <span
                  style={{
                    flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-soft)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  <AgentName handle={hit.sender} nickname={hit.sender_nickname} />: {hit.headline}
                </span>
                <span
                  style={{
                    flex: 'none', font: '400 10.5px var(--font-machine)',
                    color: 'var(--text-faint)',
                  }}
                >
                  {FOUND_BY_LABEL[hit.found_by]}
                </span>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  )
}
